import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import {
  Calculator, TrendingUp, AlertCircle, Loader2, BarChart3,
  Target, DollarSign, Sparkles, CheckCircle2, XCircle,
  ChevronRight, Landmark, Shield, ArrowRight, Clock,
  BrainCircuit, Zap, Info
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => {
  const n = typeof v === 'number' && !isNaN(v) ? v : 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
};

/* ─── DSCR Gauge ─── */
const DSCRGauge = ({ dscr, rating }) => {
  const percent = Math.min(100, Math.max(0, (dscr / 2) * 100));
  const colorMap = { emerald: 'text-emerald-600', blue: 'text-blue-600', amber: 'text-amber-600', orange: 'text-orange-600', red: 'text-red-600' };
  const bgMap = { emerald: 'bg-emerald-600', blue: 'bg-blue-600', amber: 'bg-amber-600', orange: 'bg-orange-600', red: 'bg-red-600' };

  return (
    <div className="text-center" data-testid="dscr-gauge">
      <div className="relative inline-flex items-center justify-center">
        <svg className="w-40 h-40 -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="50" fill="none" stroke="currentColor" strokeWidth="10" className="text-muted/30" />
          <circle cx="60" cy="60" r="50" fill="none" stroke="currentColor" strokeWidth="10" strokeDasharray={`${percent * 3.14} 314`} strokeLinecap="round" className={colorMap[rating?.color] || 'text-muted-foreground/70'} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={cn("text-3xl font-bold", colorMap[rating?.color])} data-testid="dscr-value">{dscr?.toFixed(2) || '—'}</span>
          <span className="text-xs text-muted-foreground">DSCR</span>
        </div>
      </div>
      <Badge className={cn("mt-2", `${bgMap[rating?.color]} text-white`)} data-testid="dscr-rating">
        {rating?.label || 'N/A'}
      </Badge>
      <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto">{rating?.message}</p>
    </div>
  );
};


/* ─── Manual Calculator ─── */
const ManualCalculator = ({ workspaceId, onResult }) => {
  const [form, setForm] = useState({
    gross_revenue: '',
    operating_expenses: '',
    existing_debt_payments: '',
    proposed_loan_payment: '',
  });
  const [period, setPeriod] = useState('annual'); // 'annual' | 'monthly'
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);

  const multiplier = period === 'monthly' ? 12 : 1;

  const handleCalc = async () => {
    if (!form.gross_revenue || !form.operating_expenses || !form.existing_debt_payments) return;
    setCalculating(true);
    try {
      const res = await apiFetch(`${API_URL}/api/dscr/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: workspaceId,
          gross_revenue: (parseFloat(form.gross_revenue) || 0) * multiplier,
          operating_expenses: (parseFloat(form.operating_expenses) || 0) * multiplier,
          existing_debt_payments: (parseFloat(form.existing_debt_payments) || 0) * multiplier,
          proposed_loan_payment: (parseFloat(form.proposed_loan_payment) || 0) * multiplier,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        onResult?.(data);
      }
    } catch (e) {
      console.error(e);
    }
    setCalculating(false);
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const periodLabel = period === 'monthly' ? 'Monthly' : 'Annual';
  const placeholder = period === 'monthly'
    ? { rev: 'e.g. 41667', exp: 'e.g. 29167', debt: 'e.g. 5000', loan: 'e.g. 2000 (optional)' }
    : { rev: 'e.g. 500000', exp: 'e.g. 350000', debt: 'e.g. 60000', loan: 'e.g. 24000 (optional)' };

  return (
    <Card data-testid="dscr-manual-calculator">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="w-5 h-5 text-violet-500" />
              DSCR Calculator
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Enter your figures to calculate your Debt Service Coverage Ratio.</p>
          </div>
          <div className="flex items-center rounded-lg border p-0.5 bg-muted" data-testid="period-toggle">
            <button
              onClick={() => setPeriod('monthly')}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                period === 'monthly'
                  ? 'bg-white dark:bg-muted shadow-sm text-violet-700 dark:text-violet-400'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              data-testid="toggle-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setPeriod('annual')}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                period === 'annual'
                  ? 'bg-white dark:bg-muted shadow-sm text-violet-700 dark:text-violet-400'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              data-testid="toggle-annual"
            >
              Annual
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">{periodLabel} Gross Revenue</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground/70" />
              <Input type="number" placeholder={placeholder.rev} className="pl-9" value={form.gross_revenue} onChange={e => updateField('gross_revenue', e.target.value)} data-testid="input-gross-revenue" />
            </div>
          </div>
          <div>
            <Label className="text-sm">{periodLabel} Operating Expenses</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground/70" />
              <Input type="number" placeholder={placeholder.exp} className="pl-9" value={form.operating_expenses} onChange={e => updateField('operating_expenses', e.target.value)} data-testid="input-operating-expenses" />
            </div>
          </div>
          <div>
            <Label className="text-sm">{periodLabel} Existing Debt Payments</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground/70" />
              <Input type="number" placeholder={placeholder.debt} className="pl-9" value={form.existing_debt_payments} onChange={e => updateField('existing_debt_payments', e.target.value)} data-testid="input-existing-debt" />
            </div>
          </div>
          <div>
            <Label className="text-sm">Proposed New Loan Payment ({periodLabel})</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground/70" />
              <Input type="number" placeholder={placeholder.loan} className="pl-9" value={form.proposed_loan_payment} onChange={e => updateField('proposed_loan_payment', e.target.value)} data-testid="input-proposed-loan" />
            </div>
          </div>
        </div>

        {period === 'monthly' && form.gross_revenue && (
          <p className="text-xs text-muted-foreground/70 flex items-center gap-1">
            <Info className="w-3 h-3" />
            Monthly values will be annualized (x12) for DSCR calculation
          </p>
        )}

        <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={handleCalc} disabled={calculating || !form.gross_revenue} data-testid="calculate-dscr-btn">
          {calculating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Calculator className="w-4 h-4 mr-1" />}
          Calculate DSCR
        </Button>

        {result && (
          <div className="mt-4 space-y-4 animate-in fade-in duration-300" data-testid="dscr-result">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1 flex justify-center">
                <DSCRGauge dscr={result.dscr} rating={result.rating} />
              </div>
              <div className="md:col-span-2 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-800">
                    <p className="text-xs text-muted-foreground">Net Operating Income</p>
                    <p className="text-lg font-bold text-emerald-600">{fmt(result.net_operating_income)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-800">
                    <p className="text-xs text-muted-foreground">Total Debt Service</p>
                    <p className="text-lg font-bold text-red-600">{fmt(result.total_debt_service)}</p>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 border space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Guidance</p>
                  <div className="flex items-start gap-2 text-sm">
                    <Target className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>NOI needed for DSCR 1.25: <span className="font-semibold">{fmt(result.guidance?.target_noi_for_1_25)}</span>
                    {result.guidance?.noi_gap_to_1_25 > 0 && <span className="text-amber-600"> (gap: {fmt(result.guidance.noi_gap_to_1_25)})</span>}
                    </span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <Landmark className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                    <span>Max additional annual loan at DSCR 1.25: <span className="font-semibold">{fmt(result.guidance?.max_additional_loan_payment_at_1_25)}</span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


/* ─── AI Counseling Panel ─── */
const AICounseling = ({ workspaceId, dscrData }) => {
  const [counseling, setCounseling] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loanPurpose, setLoanPurpose] = useState('');
  const [loanAmount, setLoanAmount] = useState('');
  const [aiBalance, setAiBalance] = useState(null);
  const [sessionError, setSessionError] = useState(null);

  // Fetch AI session balance
  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/dscr/ai-balance`);
        if (res.ok) setAiBalance(await res.json());
      } catch (e) { /* silent */ }
    };
    fetchBalance();
  }, [counseling]);

  const getCounseling = async () => {
    if (!dscrData) return;
    setLoading(true);
    setSessionError(null);
    try {
      const res = await apiFetch(`${API_URL}/api/dscr/counseling`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: workspaceId,
          dscr: dscrData.dscr,
          net_operating_income: dscrData.net_operating_income,
          total_debt_service: dscrData.total_debt_service,
          revenue: dscrData.inputs?.gross_revenue || dscrData.revenue || 0,
          expenses: dscrData.inputs?.operating_expenses || dscrData.expenses || 0,
          loan_purpose: loanPurpose,
          loan_amount: parseFloat(loanAmount) || 0,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setCounseling(data.counseling);
      } else if (res.status === 402) {
        const err = await res.json();
        setSessionError(err.detail || "No AI sessions remaining. Sessions reset with your next billing cycle.");
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  return (
    <Card data-testid="ai-counseling">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <BrainCircuit className="w-5 h-5 text-emerald-500" />
          AI Loan Counseling
        </CardTitle>
        <p className="text-sm text-muted-foreground">Get personalized advice from CFO Buck on improving your loan qualification.</p>
        {aiBalance && (
          <div className="flex items-center gap-3 mt-2" data-testid="ai-session-balance">
            <Badge variant="outline" className={`text-xs ${aiBalance.ai_sessions_remaining > 0 ? 'border-emerald-500/30 text-emerald-600' : 'border-amber-500/30 text-amber-600'}`}>
              {aiBalance.ai_sessions_remaining} / {aiBalance.ai_sessions_monthly} sessions remaining
            </Badge>
            {aiBalance.ai_sessions_used_this_period > 0 && (
              <span className="text-xs text-muted-foreground">{aiBalance.ai_sessions_used_this_period} used this period</span>
            )}
            {aiBalance.ai_overage_charges > 0 && (
              <span className="text-xs text-amber-500">+${aiBalance.ai_overage_charges.toFixed(3)} overage</span>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {!dscrData ? (
          <div className="text-center py-6 text-muted-foreground">
            <Calculator className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p>Calculate your DSCR first to get AI counseling.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Loan Purpose (optional)</Label>
                <Input placeholder="e.g. Equipment purchase, expansion" className="mt-1" value={loanPurpose} onChange={e => setLoanPurpose(e.target.value)} data-testid="input-loan-purpose" />
              </div>
              <div>
                <Label className="text-sm">Desired Loan Amount (optional)</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground/70" />
                  <Input type="number" placeholder="e.g. 250000" className="pl-9" value={loanAmount} onChange={e => setLoanAmount(e.target.value)} data-testid="input-loan-amount" />
                </div>
              </div>
            </div>

            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={getCounseling} disabled={loading} data-testid="get-counseling-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
              {aiBalance && aiBalance.ai_sessions_remaining <= 0 ? `Get AI Counseling ($${aiBalance.overage_rate}/session)` : 'Get AI Counseling'}
            </Button>

            {sessionError && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm text-amber-700 dark:text-amber-400" data-testid="session-error">
                {sessionError}
              </div>
            )}

            {loading && (
              <div className="space-y-3 animate-pulse">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-40 w-full" />
              </div>
            )}

            {counseling && !loading && (
              <div className="space-y-4 animate-in fade-in duration-300" data-testid="counseling-result">
                {/* Loan Readiness Score */}
                {counseling.loan_readiness_score !== undefined && (
                  <div className="p-4 rounded-xl border bg-gradient-to-r from-slate-50 to-violet-50 dark:from-slate-800/50 dark:to-violet-900/10">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold">Loan Readiness Score</span>
                      <Badge className={cn(
                        counseling.loan_readiness_score >= 70 ? 'bg-emerald-600' : counseling.loan_readiness_score >= 40 ? 'bg-amber-600' : 'bg-red-600',
                        'text-white'
                      )} data-testid="readiness-score">
                        {counseling.loan_readiness_score}/100
                      </Badge>
                    </div>
                    <Progress value={counseling.loan_readiness_score} className="h-2" />
                  </div>
                )}

                {/* Overall Assessment */}
                <div className="p-4 rounded-xl border bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800">
                  <p className="text-sm font-semibold text-blue-700 dark:text-blue-400 mb-1 flex items-center gap-1.5">
                    <Info className="w-4 h-4" /> Overall Assessment
                  </p>
                  <p className="text-sm text-foreground/80">{counseling.overall_assessment}</p>
                </div>

                {/* Lender Perspective */}
                {counseling.lender_perspective && (
                  <div className="p-4 rounded-xl border bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800">
                    <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-1 flex items-center gap-1.5">
                      <Landmark className="w-4 h-4" /> Lender's Perspective
                    </p>
                    <p className="text-sm text-foreground/80">{counseling.lender_perspective}</p>
                  </div>
                )}

                {/* Strengths & Risks */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {counseling.strengths?.length > 0 && (
                    <div className="p-3 rounded-lg border bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800">
                      <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-2 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Strengths
                      </p>
                      <ul className="space-y-1">
                        {counseling.strengths.map((s, i) => (
                          <li key={i} className="text-xs text-foreground/80 flex items-start gap-1.5">
                            <ChevronRight className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" /> {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {counseling.risks?.length > 0 && (
                    <div className="p-3 rounded-lg border bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800">
                      <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-2 flex items-center gap-1">
                        <XCircle className="w-3.5 h-3.5" /> Risk Factors
                      </p>
                      <ul className="space-y-1">
                        {counseling.risks.map((r, i) => (
                          <li key={i} className="text-xs text-foreground/80 flex items-start gap-1.5">
                            <AlertCircle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" /> {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Action Items */}
                {counseling.action_items?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold flex items-center gap-1.5">
                      <Zap className="w-4 h-4 text-amber-500" /> Action Items
                    </p>
                    {counseling.action_items.map((item, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/30 transition-colors">
                        <div className={cn(
                          'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0',
                          item.priority === 'high' ? 'bg-red-600 text-white' : item.priority === 'medium' ? 'bg-amber-500 text-white' : 'bg-muted text-foreground'
                        )}>
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{item.action}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{item.impact}</p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline" className="text-[9px]">{item.priority}</Badge>
                            {item.timeframe && <Badge variant="outline" className="text-[9px]"><Clock className="w-2.5 h-2.5 mr-0.5" />{item.timeframe}</Badge>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Recommended Loan Types */}
                {counseling.recommended_loan_types?.length > 0 && (
                  <div className="p-3 rounded-lg border bg-violet-50 dark:bg-violet-900/10 border-violet-200 dark:border-violet-800">
                    <p className="text-xs font-semibold text-violet-700 dark:text-violet-400 mb-2 flex items-center gap-1.5">
                      <Shield className="w-3.5 h-3.5" /> Recommended Loan Types
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {counseling.recommended_loan_types.map((t, i) => (
                        <Badge key={i} variant="outline" className="text-xs border-violet-300 text-violet-700 dark:border-violet-700 dark:text-violet-400">{t}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Improvement Plan */}
                {counseling.improvement_plan && (
                  <div className="p-4 rounded-xl border bg-muted/50">
                    <p className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                      <TrendingUp className="w-4 h-4 text-emerald-500" /> Improvement Plan
                    </p>
                    <p className="text-sm text-foreground/80 whitespace-pre-line">{counseling.improvement_plan}</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};


/* ═══ MAIN DSCR PAGE ═══ */
export default function DSCRCounseling() {
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.workspace_id;
  const [autoData, setAutoData] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [latestResult, setLatestResult] = useState(null);

  const fetchAutoData = useCallback(async () => {
    if (!workspaceId) return;
    setAutoLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/dscr/auto/${workspaceId}`);
      if (res.ok) {
        const data = await res.json();
        setAutoData(data);
        if (data.has_financial_data && data.dscr > 0) setLatestResult(data);
      }
    } catch { /* ignore */ }
    setAutoLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchAutoData(); }, [fetchAutoData]);

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="dscr-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-violet-500" />
          Cash Flow &amp; Loan Qualification
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Analyze your Debt Service Coverage Ratio (DSCR) and get AI-powered counseling on improving your loan qualification.
        </p>
      </div>

      {/* Auto-detected DSCR from financials */}
      {autoLoading ? (
        <Card><CardContent className="p-6"><Skeleton className="h-32 w-full" /></CardContent></Card>
      ) : autoData?.has_financial_data && autoData.dscr > 0 ? (
        <Card className="border-violet-200 dark:border-violet-800 bg-gradient-to-r from-violet-50/50 to-transparent dark:from-violet-900/10" data-testid="auto-dscr-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-semibold text-violet-700 dark:text-violet-400 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4" /> Auto-Detected from Your Books
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">Based on last 12 months of journal entries</p>
              </div>
              <DSCRGauge dscr={autoData.dscr} rating={autoData.rating} />
            </div>
            <div className="grid grid-cols-4 gap-3 mt-4">
              <div className="text-center p-2 rounded bg-card border">
                <p className="text-lg font-bold text-emerald-600">{fmt(autoData.revenue)}</p>
                <p className="text-[10px] text-muted-foreground">Revenue</p>
              </div>
              <div className="text-center p-2 rounded bg-card border">
                <p className="text-lg font-bold text-red-500">{fmt(autoData.expenses)}</p>
                <p className="text-[10px] text-muted-foreground">Expenses</p>
              </div>
              <div className="text-center p-2 rounded bg-card border">
                <p className="text-lg font-bold text-blue-600">{fmt(autoData.net_operating_income)}</p>
                <p className="text-[10px] text-muted-foreground">NOI</p>
              </div>
              <div className="text-center p-2 rounded bg-card border">
                <p className="text-lg font-bold text-amber-600">{fmt(autoData.total_debt_service)}</p>
                <p className="text-[10px] text-muted-foreground">Annual Debt</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : autoData && !autoData.has_financial_data ? (
        <Card className="border-amber-200 dark:border-amber-800">
          <CardContent className="p-5 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-700 dark:text-amber-400">No Financial Data Yet</p>
              <p className="text-sm text-amber-600 dark:text-amber-500 mt-1">
                Add journal entries or connect bank accounts to see your auto-calculated DSCR. You can still use the manual calculator below.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Tabs defaultValue="calculator" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 max-w-sm">
          <TabsTrigger value="calculator" data-testid="tab-calculator">
            <Calculator className="w-4 h-4 mr-1" /> Calculator
          </TabsTrigger>
          <TabsTrigger value="counseling" data-testid="tab-counseling">
            <BrainCircuit className="w-4 h-4 mr-1" /> AI Counseling
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calculator">
          {workspaceId ? (
            <ManualCalculator workspaceId={workspaceId} onResult={setLatestResult} />
          ) : (
            <Card><CardContent className="p-8 text-center text-muted-foreground">Select a workspace first.</CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="counseling">
          {workspaceId ? (
            <AICounseling workspaceId={workspaceId} dscrData={latestResult} />
          ) : (
            <Card><CardContent className="p-8 text-center text-muted-foreground">Select a workspace first.</CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* DSCR Reference */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
            <Info className="w-4 h-4" /> Understanding DSCR
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-2 text-center text-xs">
            {[
              { range: '< 0.8', label: 'Critical', color: 'bg-red-600 text-white' },
              { range: '0.8 - 1.0', label: 'Needs Work', color: 'bg-orange-500 text-white' },
              { range: '1.0 - 1.25', label: 'Fair', color: 'bg-amber-500 text-white' },
              { range: '1.25 - 1.5', label: 'Good', color: 'bg-blue-500 text-white' },
              { range: '1.5+', label: 'Excellent', color: 'bg-emerald-600 text-white' },
            ].map(r => (
              <div key={r.range} className={cn('rounded-lg p-2.5', r.color)}>
                <p className="font-bold text-sm">{r.range}</p>
                <p className="text-white/90 text-[11px]">{r.label}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3 text-center">
            DSCR = Net Operating Income / Total Debt Service. Most lenders require a minimum DSCR of 1.25.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
